
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSecureAdminAuth } from "@/hooks/useSecureAdminAuth";
import {
  BarChart3,
  BookOpen,
  FileText,
  Gift,
  Globe,
  Heart,
  Home,
  Settings,
  ShoppingCart,
  Users,
  Video,
  Clock,
  DollarSign,
  Tag,
  MessageCircle,
  Zap,
  Trophy,
  Printer,
  CalendarDays
} from "lucide-react";
import { Button } from "@/components/ui/button";
import StoryManager from "@/components/admin/StoryManager";
import UserManager from "@/components/admin/UserManager";
import BlogManager from "@/components/admin/BlogManager";
import BlogCategoryManager from "@/components/admin/BlogCategoryManager";
import PrintablesManager from "@/components/admin/PrintablesManager";
import PurchaseHistoryManager from "@/components/admin/PurchaseHistoryManager";
import CommunityHelpersManager from "@/components/admin/CommunityHelpersManager";
import WorldContentManager from "@/components/admin/WorldContentManager";
import HomePageSettingsManager from "@/components/admin/HomePageSettingsManager";
import PrintablesSettingsManager from "@/components/admin/PrintablesSettingsManager";
import CurrencySettingsManager from "@/components/admin/CurrencySettingsManager";
import HistoryManager from "@/components/admin/HistoryManager";
import FreeSamplesManager from "@/components/admin/FreeSamplesManager";
import DailyTipsManager from "@/components/admin/DailyTipsManager";
import VideoOfTheDayManager from "@/components/admin/VideoOfTheDayManager";
import SpellingsManager from "@/components/admin/SpellingsManager";
import SentencesManager from "@/components/admin/SentencesManager";
import YouTubeGalleryManager from "@/components/admin/YouTubeGalleryManager";
import AdminOverview from "@/components/admin/AdminOverview";
import {
  DailyChallengesManager,
  WeeklyStoryManager,
  PrintableFridayManager,
  WeeklyQuizzesManager,
} from "@/components/admin/RetentionManager";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [selectedSection, setSelectedSection] = useState("overview");
  const { adminUser, isAuthenticated, isLoading, logout } = useSecureAdminAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      console.log("Admin not authenticated, redirecting to login");
      navigate('/admin-portal-2024');
    }
  }, [isAuthenticated, isLoading, navigate]);

  const handleSignOut = () => {
    logout();
    navigate('/admin-portal-2024');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  const renderContent = () => {
    switch (selectedSection) {
      case 'overview':
        return <AdminOverview />;
      case 'homepage-settings':
        return <HomePageSettingsManager />;
      case 'stories':
        return <StoryManager />;
      case 'blog':
        return <BlogManager />;
      case 'blog-categories':
        return <BlogCategoryManager />;
      case 'youtube-gallery':
        return <YouTubeGalleryManager />;
      case 'users':
        return <UserManager />;
      case 'printables':
        return <PrintablesManager />;
      case 'printables-settings':
        return <PrintablesSettingsManager />;
      case 'currency-settings':
        return <CurrencySettingsManager />;
      case 'purchase-history':
        return <PurchaseHistoryManager />;
      case 'community-helpers':
        return <CommunityHelpersManager />;
      case 'world-content':
        return <WorldContentManager />;
      case 'history':
        return <HistoryManager />;
      case 'free-samples':
        return <FreeSamplesManager />;
      case 'daily-tips':
        return <DailyTipsManager />;
      case 'video-of-the-day':
        return <VideoOfTheDayManager />;
      case 'spellings':
        return <SpellingsManager />;
      case 'sentences':
        return <SentencesManager />;
      case 'daily-challenges':
        return <DailyChallengesManager />;
      case 'weekly-story':
        return <WeeklyStoryManager />;
      case 'printable-friday':
        return <PrintableFridayManager />;
      case 'weekly-quizzes':
        return <WeeklyQuizzesManager />;
      default:
        return <AdminOverview />;
    }
  };

  const sidebarItems = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'homepage-settings', label: 'HomePage Settings', icon: Home },
    { id: 'stories', label: 'Stories', icon: BookOpen },
    { id: 'blog', label: 'Blog Posts', icon: FileText },
    { id: 'blog-categories', label: 'Blog Categories', icon: Tag },
    { id: 'youtube-gallery', label: 'YouTube Gallery', icon: Video },
    { id: 'users', label: 'Users', icon: Users },
    { id: 'printables', label: 'Printables', icon: FileText },
    { id: 'printables-settings', label: 'Printables Settings', icon: Settings },
    { id: 'currency-settings', label: 'Currency & Payments', icon: DollarSign },
    { id: 'purchase-history', label: 'Purchase History', icon: ShoppingCart },
    { id: 'community-helpers', label: 'Community Helpers', icon: Heart },
    { id: 'world-content', label: 'World Content', icon: Globe },
    { id: 'history', label: 'History', icon: Clock },
    { id: 'free-samples', label: 'Free Samples', icon: Gift },
    { id: 'daily-tips', label: 'Daily Tips', icon: BookOpen },
    { id: 'video-of-the-day', label: 'Video of the Day', icon: Video },
    { id: 'spellings', label: 'Spellings', icon: BookOpen },
    { id: 'sentences', label: 'Sentences of the Day', icon: MessageCircle },
    { id: 'daily-challenges', label: 'Daily Challenges', icon: Zap },
    { id: 'weekly-story', label: 'Story of the Week', icon: BookOpen },
    { id: 'printable-friday', label: 'Printable Friday', icon: Printer },
    { id: 'weekly-quizzes', label: 'Weekly Quizzes', icon: Trophy },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="flex h-screen overflow-hidden">
        {/* Sidebar */}
        <div className="w-64 shrink-0 h-screen overflow-y-auto bg-gray-800 text-white py-6 px-3 flex flex-col">
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-center">Admin Panel</h1>
            <p className="text-center text-sm text-gray-300 mt-2">Welcome, {adminUser?.username}</p>
          </div>
          <nav className="flex-grow">
            <ul>
              {sidebarItems.map((item) => (
                <li key={item.id} className="mb-2">
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-white hover:bg-gray-700 hover:text-white ${
                      selectedSection === item.id ? "bg-gray-700 text-white" : ""
                    }`}
                    onClick={() => setSelectedSection(item.id)}
                  >
                    <item.icon className="w-4 h-4 mr-2" />
                    {item.label}
                  </Button>
                </li>
              ))}
            </ul>
          </nav>
          <div className="mt-8">
            <Button
              variant="secondary"
              className="w-full"
              onClick={handleSignOut}
            >
              Sign Out
            </Button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
